import type Phaser from "phaser";
import type { CharacterManifest } from "./character.ts";
import { stripKey } from "./character.ts";
import { headAboveFeet } from "./creature-head.ts";
import { HEAD_ABOVE_FEET } from "./world-ui/characters.ts";

/**
 * How far each creature sheet's art reaches above its feet (R-0089), measured once per sheet from its idle strip
 * facing right, the strip a creature stands in most (a creature faces only left or right, and left mirrors right about
 * the pivot column, so both reach as high), and kept for the page's life. The nameplate, the health bar, the damage
 * numbers and the target's top stand over it.
 */
export class CreatureHeads {
  private readonly measured = new Map<string, number>();
  private warned = false;

  /**
   * @param textures The scene's texture manager, holding every strip of the character manifest.
   */
  public constructor(private readonly textures: Phaser.Textures.TextureManager) {}

  /**
   * How far a sheet's art reaches above its feet.
   *
   * @param manifest The character manifest.
   * @param sheet The sheet's name.
   * @returns The measured height in world pixels, or the default `HEAD_ABOVE_FEET` when the sheet or its idle
   *   strip is not loaded or its pixels cannot be read.
   */
  public of(manifest: CharacterManifest, sheet: string): number {
    const known = this.measured.get(sheet);
    if (known !== undefined) {
      return known;
    }
    const head = this.measure(manifest, sheet);
    this.measured.set(sheet, head);
    return head;
  }

  /**
   * Reads a sheet's idle strip into a canvas and measures it.
   *
   * @param manifest The character manifest.
   * @param sheet The sheet's name.
   * @returns The height, or `HEAD_ABOVE_FEET` when it cannot be measured, such as a strip the browser will not let the
   *   page read; the first such sheet is logged once a page.
   */
  private measure(manifest: CharacterManifest, sheet: string): number {
    const description = manifest.characters[sheet];
    const key = stripKey(sheet, "idle", "right");
    if (description === undefined || !this.textures.exists(key)) {
      return HEAD_ABOVE_FEET;
    }
    const source = this.textures.get(key).getSourceImage() as CanvasImageSource & { readonly width: number; readonly height: number };
    const canvas = document.createElement("canvas");
    canvas.width = source.width;
    canvas.height = source.height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (context === null || canvas.width === 0 || canvas.height === 0) {
      return HEAD_ABOVE_FEET;
    }
    let pixels: Uint8ClampedArray;
    try {
      context.drawImage(source, 0, 0);
      pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    } catch (error) {
      if (!this.warned) {
        this.warned = true;
        console.warn(`the ${sheet} sheet's idle strip could not be read (${String(error)}); its plate stands at the default head height`);
      }
      return HEAD_ABOVE_FEET;
    }
    return headAboveFeet(pixels, canvas.width, canvas.height, description.frameHeight, description.pivot.y);
  }
}
